import {
  EntityDetail,
  MultiTenantLocationDetails,
} from '@user/store/map/typings'

type PatchArgs = {
  entityId: number
  serviceLocationId: number
  isDraftLead: boolean
  newReason: string | null
  entityDetails: EntityDetail | null
  serviceLocationsDetails: MultiTenantLocationDetails[]
  knockStatusLocationId?: number | null
}

/**
 * SH-1200: the single-entity drawer/bubble and the multi-tenant list drawer
 * are backed by two independent Redux slices (`entityDetails` and
 * `selectedServiceLocationsDetails`). If both happen to be open at once for
 * the same record, saving a reason from either surface must patch BOTH so
 * they stay in sync live, instead of one going stale until its next refetch.
 *
 * Per SH-1322, a knock status belongs to a physical location, not to an
 * individual Lead/Opp/DraftLead record -- multiple co-located records share
 * one underlying KnockStatusHistory row. So the multi-tenant list patch
 * matches every row sharing the edited row's `knockStatusLocationId`, not
 * just the entityId/serviceLocationId of the record that was actually
 * clicked, otherwise other co-located rows in the same list go stale until
 * a refetch even though they display the same shared knock status.
 */
export function buildKnockStatusReasonPatches({
  entityId,
  serviceLocationId,
  isDraftLead,
  newReason,
  entityDetails,
  serviceLocationsDetails,
  knockStatusLocationId,
}: PatchArgs): {
  patchedEntityDetails: EntityDetail | null
  patchedServiceLocationsDetails: MultiTenantLocationDetails[] | null
} {
  let patchedEntityDetails: EntityDetail | null = null

  if (entityDetails && entityDetails.id === entityId) {
    if (!isDraftLead) {
      patchedEntityDetails = {
        ...entityDetails,
        locations: {
          ...entityDetails.locations,
          serviceLocations: entityDetails.locations.serviceLocations.map(
            (loc) => {
              if (loc.id === serviceLocationId && loc.knockStatusDetails) {
                return {
                  ...loc,
                  knockStatusDetails: {
                    ...loc.knockStatusDetails,
                    mostRecentNote: newReason || '',
                  },
                }
              }
              return loc
            },
          ),
        },
      } as EntityDetail
    } else if (entityDetails.knockStatusDetails) {
      patchedEntityDetails = {
        ...entityDetails,
        knockStatusDetails: {
          ...entityDetails.knockStatusDetails,
          mostRecentNote: newReason || '',
        },
      } as EntityDetail
    }
  }

  let patchedServiceLocationsDetails: MultiTenantLocationDetails[] | null = null

  if (serviceLocationsDetails.length > 0) {
    patchedServiceLocationsDetails = serviceLocationsDetails.map((loc) => {
      if (!loc.knockStatusDetails) return loc

      const isSameSharedLocation =
        knockStatusLocationId != null &&
        loc.knockStatusDetails.knockStatusLocationId === knockStatusLocationId

      const isEditedRow =
        loc.entityId === entityId && loc.serviceLocationId === serviceLocationId

      if (!isSameSharedLocation && !isEditedRow) return loc

      return {
        ...loc,
        knockStatusDetails: {
          ...loc.knockStatusDetails,
          mostRecentNote: newReason || '',
        },
      }
    })
  }

  return { patchedEntityDetails, patchedServiceLocationsDetails }
}
